import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import httpClient from "../../utils/httpClients";
import Swal from "sweetalert2";

function Album() {
  // Main state from upper code
  const [albums, setAlbums] = useState([]);
  const [period, setPeriod] = useState("week");
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showEditSongDialog, setShowEditSongDialog] = useState(false);
  const [editingSong, setEditingSong] = useState({});
  const [uploading, setUploading] = useState(false);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [songs, setSongs] = useState([]);
  const [editSongErrors, setEditSongErrors] = useState({});

  // State for API data and pagination
  const [themes, setThemes] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loadingThemes, setLoadingThemes] = useState(false);
  const [loadingAlbums, setLoadingAlbums] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalAlbums, setTotalAlbums] = useState(0);
  const [artistIds, setArtistIds] = useState([]);

  const limit = 10;

  // State for album details modal
  const [selectedAlbumDetails, setSelectedAlbumDetails] = useState(null);
  const [loadingAlbumDetails, setLoadingAlbumDetails] = useState(false);
  const [albumSongs, setAlbumSongs] = useState([]);
  const [loadingAlbumSongs, setLoadingAlbumSongs] = useState(false);

  // Form data state (Upload Album modal)
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    releaseDate: "",
    artist: "",
    themes: "",
    coverImage: null,
    file: null,
  });
  const [formErrors, setFormErrors] = useState({});
  const [uploadFormErrors, setUploadFormErrors] = useState({
    coverImage: "",
    file: "",
  });

  // Edit Album modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingAlbum, setEditingAlbum] = useState(null);
  const [editFormData, setEditFormData] = useState({
    title: "",
    description: "",
    artist: "",
    themes: "",
    releaseDate: "",
  });
  const [editFormErrors, setEditFormErrors] = useState({});
  const [editUploadFormErrors, setEditUploadFormErrors] = useState({
    coverImage: "",
    file: "",
  });
  const [editCoverImageFile, setEditCoverImageFile] = useState(null);
  const [editAlbumFile, setEditAlbumFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [editErrors, setEditErrors] = useState({});
  const [songErrors, setSongErrors] = useState([]);

  // Fetch themes and albums on component mount
  useEffect(() => {
    fetchThemes();
    fetchAlbums();
  }, [currentPage, searchQuery]);

  // Close modals on Escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key !== "Escape") return;
      if (showUploadDialog) {
        setShowUploadDialog(false);
        resetForm();
      }
      if (showEditDialog) {
        setShowEditDialog(false);
        setSongs([]);
        setSelectedAlbum(null);
      }
      if (isEditModalOpen) closeEditModal();
      if (selectedAlbumDetails) setSelectedAlbumDetails(null);
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [showUploadDialog, showEditDialog, isEditModalOpen, selectedAlbumDetails]);

  useEffect(() => {
    getArtistIdS();
  }, []);

  const getArtistIdS = async () => {
    try {
      const response = await httpClient.get(`artist/misc/get-artist`);
      const artistIds = response.data?.data.artist?._id;
      setArtistIds(artistIds);

      if (response.data.success) {
      } else {
        console.log("Data success false hai:", response.data.message);
      }
    } catch (error) {
      console.error("Catch Block Error:", error);
    }
  };

  const fetchAlbums = async (page = 1, search = "") => {
    try {
      setLoadingAlbums(true);
      const response = await httpClient.get(
        `admin/media-collection/artist-get-all?page=${page}&limit=${limit}&contentType=album${search ? `&artist=${search}` : ""}`,
      );

      const data = response.data;
      if (data.success) {
        setAlbums(data.data || []);
        // Use totalCount or total from API response, fallback to data length
        const totalItems =
          data.totalCount || data.total || data.data?.length || 0;
        setTotalPages(Math.ceil(totalItems / limit) || 1);
        setTotalAlbums(totalItems);
      } else {
        throw new Error(data.message || "Failed to fetch albums");
      }
    } catch (error) {
      console.error("Error fetching albums:", error);
      setAlbums([]);
      setTotalPages(1);
      setTotalAlbums(0);
    } finally {
      setLoadingAlbums(false);
    }
  };

  const fetchThemes = async () => {
    try {
      setLoadingThemes(true);
      const response = await httpClient.get("admin/theme/get-all-theme");
      setThemes(response.data.data?.getThemes || []);
    } catch (error) {
      console.error("Error fetching themes:", error);
      setThemes([]);
    } finally {
      setLoadingThemes(false);
    }
  };

  // Fetch album details by ID (get-by-id may return array of songs or album object)
  const fetchAlbumDetails = async (album) => {
    const albumId = album?._id;
    if (!albumId) return;
    try {
      setLoadingAlbumDetails(true);
      setLoadingAlbumSongs(true);
      const response = await httpClient.get(
        `admin/media-collection/get-by-id/${albumId}`,
      );

      const data = response.data;
      if (!data.success) {
        throw new Error(data.message || "Failed to fetch album details");
      }

      const raw = data.data;
      if (Array.isArray(raw)) {
        setAlbumSongs(raw);
        setSelectedAlbumDetails(album);
      } else {
        setSelectedAlbumDetails(raw);
        const songsFromPayload = raw?.songs ?? raw?.tracks ?? [];
        if (songsFromPayload.length > 0) {
          setAlbumSongs(songsFromPayload);
        } else {
          await fetchAlbumSongs(albumId);
        }
      }
    } catch (error) {
      console.error("Error fetching album details:", error);
      setSelectedAlbumDetails(null);
      setAlbumSongs([]);
    } finally {
      setLoadingAlbumDetails(false);
      setLoadingAlbumSongs(false);
    }
  };

  // Fetch songs for a specific album
  const fetchAlbumSongs = async (albumId) => {
    try {
      setLoadingAlbumSongs(true);
      const response = await httpClient.get(
        `admin/media-collection/get-by-id/${albumId}`,
      );

      const data = response.data;
      if (data.success) {
        setAlbumSongs(data.data || []);
      } else {
        throw new Error(data.message || "Failed to fetch album songs");
      }
    } catch (error) {
      console.error("Error fetching album songs:", error);
      setAlbumSongs([]);
    } finally {
      setLoadingAlbumSongs(false);
    }
  };

  console.log("selectedAlbum", selectedAlbum);
  // Handle view album details
  const handleViewClick = async (album) => {
    try {
      setSelectedAlbum(album);
      await fetchAlbumDetails(album);
    } catch (error) {
      console.error("Error viewing album:", error);
    }
  };

  // Handle approve album
  const handleApprove = async (albumId) => {
    try {
      await httpClient.patch(
        `admin/media-collection/approve-collection?collectionId=${albumId}&requestStatus=approved`,
      );
      setAlbums(
        albums.map((a) =>
          a._id === albumId ? { ...a, isApproved: "approved" } : a,
        ),
      );
      if (selectedAlbumDetails && selectedAlbumDetails._id === albumId) {
        setSelectedAlbumDetails({
          ...selectedAlbumDetails,
          isApproved: "approved",
        });
      }
    } catch (error) {
      console.error("Error approving album:", error);
    }
  };

  // Handle reject album
  const handleReject = async (albumId) => {
    try {
      await httpClient.patch(
        `admin/media-collection/approve-collection?collectionId=${albumId}&requestStatus=rejected`,
      );
      setAlbums(albums.filter((a) => a._id !== albumId));
      if (selectedAlbumDetails && selectedAlbumDetails._id === albumId) {
        setSelectedAlbumDetails(null);
      }
    } catch (error) {
      console.error("Error rejecting album:", error);
    }
  };

  // Handle delete album
  const handleDelete = async (albumId) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Are you sure you want to delete this song?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });
    if (result.isConfirmed)
      try {
        await httpClient.delete(`admin/media-collection/delete/${albumId}`);
        setAlbums(albums.filter((a) => a._id !== albumId));
        if (selectedAlbumDetails && selectedAlbumDetails._id === albumId) {
          setSelectedAlbumDetails(null);
        }
        setTotalAlbums((prev) => prev - 1);
      } catch (error) {
        console.error("Error deleting album:", error);
      }
  };

  const handleEditClick = (album) => {
    setSelectedAlbum(album);
    setShowEditDialog(true);
    setSongs([]);
  };

  // Edit Album modal: fetch details for cover/file URLs then open
  const openEditModal = async (album) => {
    let albumWithDetails = { ...album };
    try {
      const response = await httpClient.get(
        `admin/media-collection/get-by-id/${album._id}`,
      );
      const data = response.data?.data;
      if (data && typeof data === "object" && !Array.isArray(data)) {
        albumWithDetails = {
          ...album,
          coverImageUrl: data.coverImageUrl ?? album.coverImageUrl,
          fileUrl: data.fileUrl ?? album.fileUrl,
        };
      }
    } catch (_) {
      /* use list data only */
    }
    setEditingAlbum(albumWithDetails);
    const themeId = Array.isArray(album.themes)
      ? album.themes[0]
      : album.themes || "";
    setEditFormData({
      title: album.title || "",
      description: album.description || "",
      artist: getArtistName(album),
      themes: themeId,
      releaseDate:
        album.releaseDate?.split?.("T")[0] || album.releaseDate || "",
      // artistIds: artistIds,
    });
    setIsEditModalOpen(true);
    setEditCoverImageFile(null);
    setEditAlbumFile(null);
    setEditFormErrors({});
    setEditUploadFormErrors({ coverImage: "", file: "" });
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setEditingAlbum(null);
    setEditCoverImageFile(null);
    setEditAlbumFile(null);
    setEditFormErrors({});
    setEditUploadFormErrors({ coverImage: "", file: "" });
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({ ...prev, [name]: value }));
    if (editFormErrors[name]) {
      setEditFormErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };
  const validateSongs = () => {
    const newErrors = songs.map((song) => {
      let errors = {};
      if (!song.title?.trim()) errors.title = "Song title is required.";
      if (song.title.length > 100)
        errors.title = "Song title should be less than 100 characters.";
      if (!song.recordingVersionTitle)
        errors.recordingVersionTitle = "Select recording version title.";
      if (!song.themes?.[0]) errors.themes = "Theme is required.";
      if (!song.file) errors.file = "Audio file is required.";

      // if (
      //   !editingSong.releaseDate ||
      //   String(editingSong.releaseDate).trim() === ""
      // ) {
      //   errors.releaseDate = "Release date is required.";
      // }

      if (!song.coverImage) errors.coverImage = "Cover image is required.";
      console.log("new errors-", errors);
      console.log(
        "releaseDate value:",
        song.releaseDate,
        typeof song.releaseDate,
      );

      return errors;
    });

    setSongErrors([...newErrors]);
    const hasErrors = newErrors.some((err) => Object.keys(err).length > 0);
    return !hasErrors;
  };

  const validateEditSong = () => {
    const errors = {};

    // Text Fields
    if (!editingSong.title?.trim()) errors.title = "Song title is required.";
    if (editingSong.title.length > 100)
      errors.title = "Song title should be less than 100 characters.";

    if (!editingSong.recordingVersionTitle)
      errors.recordingVersionTitle = "Please select recording version title.";
    if (!editingSong.releaseDate)
      errors.releaseDate = "Release date is required.";
    if (!editingSong.description?.trim())
      errors.description = "Description is required.";

    // Theme Validation
    if (
      !editingSong.themes ||
      editingSong.themes.length === 0 ||
      !editingSong.themes[0]
    ) {
      errors.themes = "Please select a theme";
    }

    if (
      !editingSong.coverImage &&
      !editingSong.coverImageUrl &&
      !editingSong.image
    ) {
      errors.coverImage = "Cover image is required";
    }

    // Audio File Validation
    if (!editingSong.file && !editingSong.fileUrl) {
      errors.audioFile = "Audio file is required";
    }

    setEditSongErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateEditForm = () => {
    const errors = {};
    if (!editFormData.title?.trim()) errors.title = "Album title is required.";
    if (!editFormData.themes) {
      errors.themes = "Album theme is required.";
    }
    if (!editFormData.description?.trim())
      errors.description = "Description is required.";
    if (!editFormData.releaseDate)
      errors.releaseDate = "Release date is required.";
    setEditFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!validateEditForm()) return;
    setSubmitting(true);
    try {
      const updateFormData = new FormData();
      updateFormData.append("title", editFormData.title);
      updateFormData.append("description", editFormData.description);
      updateFormData.append("releaseDate", editFormData.releaseDate);
      updateFormData.append("artistIds", artistIds);
      updateFormData.append("isFeatured", "false");
      updateFormData.append("isActive", "true");
      if (editFormData.themes) {
        updateFormData.append("themes", editFormData.themes);
      }
      if (editCoverImageFile) {
        updateFormData.append("coverImage", editCoverImageFile);
      }
      if (editAlbumFile) {
        updateFormData.append("file", editAlbumFile);
      }
      const response = await httpClient.put(
        `admin/media-collection/update/${editingAlbum._id}`,
        updateFormData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      if (response.data?.success) {
        closeEditModal();
        await fetchAlbums(currentPage, searchQuery);
      } else {
        console.error("Failed to update album:", response.data?.message);
      }
    } catch (error) {
      console.error("Error updating album:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchAlbums(1, searchQuery);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    fetchAlbums(newPage, searchQuery);
  };

  // Add new song to the table
  const handleAddSong = () => {
    const newSong = {
      id: Date.now(), // temporary ID
      contentType: "song",
      title: "",
      parentId: selectedAlbum?._id || "",
      isAlbum: true,
      artistIds: selectedAlbum?.artistIds || [],
      themes: [],
      description: "",
      coverImage: null,
      file: null,
      releaseDate:
        selectedAlbum?.releaseDate || new Date().toISOString().split("T")[0],
      isActive: true,
    };
    setSongs([...songs, newSong]);
  };

  // Update song field
  const handleSongChange = (index, field, value) => {
    const updatedSongs = [...songs];
    updatedSongs[index][field] = value;
    setSongs(updatedSongs);
  };

  // Remove song from table
  const handleRemoveSong = (index) => {
    const updatedSongs = songs.filter((_, i) => i !== index);
    setSongs(updatedSongs);
  };

  // Handle file upload for songs
  const handleSongFileChange = (index, field, file) => {
    const updatedSongs = [...songs];

    if (field === "file" && file) {
      updatedSongs[index].file = file;
      updatedSongs[index].filePreview = URL.createObjectURL(file);
    }

    if (field === "coverImage" && file) {
      updatedSongs[index].coverImage = file;
      updatedSongs[index].coverPreview = URL.createObjectURL(file);
    }

    setSongs(updatedSongs);
  };

  // Submit songs for the album
  const handleSubmitSongs = async (e) => {
    e.preventDefault();

    if (!validateSongs()) return;

    setUploading(true);

    try {
      const uploadPromises = songs.map(async (song) => {
        const formData = new FormData();

        formData.append("contentType", "song");
        formData.append("title", song.title);
        formData.append("parentId", selectedAlbum._id);
        formData.append("isAlbum", "true");
        formData.append("description", song.description);
        formData.append("releaseDate", song.releaseDate);
        formData.append("isActive", "true");
        formData.append("isFeatured", "false");
        if (song.writers) {
          const writersArray = String(song.writers)
            .split(",")
            .map((s) => s.trim());
          writersArray.forEach((w) => formData.append("writers", w));
        }

        if (song.writersIPICode) {
          const ipiArray = String(song.writersIPICode)
            .split(",")
            .map((s) => s.trim());
          ipiArray.forEach((i) => formData.append("writersIPICode", i));
        }
        // formData.append("releaseDate", song.releaseDate);
        formData.append("alternativeTitle", song.alternativeTitle || "");
        formData.append(
          "recordingVersionTitle",
          song.recordingVersionTitle || "",
        );
        formData.append(
          "recordingRadioProducer",
          song.recordingRadioProducer || "",
        );
        formData.append("catalogNumber", song.catalogNumber || "");
        formData.append("LabelName", song.LabelName || "");
        formData.append("writersIPICode", song.writersIPICode || "");
        formData.append("iswc", song.iswc || "");
        formData.append("mlcSongCode", song.mlcSongCode || "");
        formData.append("upcEan", song.upcEan || "");
        formData.append("isrc", song.isrc || "");
        formData.append("artistIds", artistIds);

        // if (song.artistIds && song.artistIds.length > 0) {
        //   song.artistIds.forEach((artistId) => {
        //     formData.append("artistIds", artistId._id || artistId);
        //   });
        // }

        if (song.themes && song.themes.length > 0) {
          song.themes.forEach((themeId) => {
            formData.append("themes", themeId);
          });
        }

        if (song.coverImage) {
          formData.append("coverImage", song.coverImage);
        }
        if (song.file) {
          formData.append("file", song.file);
        }

        return httpClient.post("admin/media-track/add-track", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
      });

      await Promise.all(uploadPromises);
      setShowEditDialog(false);
      setSongs([]);
      setSelectedAlbum(null);
      await fetchAlbums(currentPage, searchQuery);
    } catch (error) {
      console.error("Error uploading songs:", error);
    } finally {
      setUploading(false);
    }
  };

  const handleEditSongs = async (e) => {
    e.preventDefault();

    const isValid = validateEditSong();

    if (!isValid) {
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();

      formData.append("contentType", "song");
      formData.append("title", editingSong.title);
      formData.append("parentId", selectedAlbum._id);
      formData.append("isAlbum", "true");
      // formData.append("artist", editingSong.artist);
      formData.append("description", editingSong.description);
      formData.append("releaseDate", editingSong.releaseDate);
      formData.append("isActive", "true");
      formData.append("isFeatured", "false");
      formData.append("artistIds", artistIds);
      formData.append("writers", editingSong.writers || "");
      formData.append("writersIPICode", editingSong.writersIPICode || "");
      formData.append(
        "recordingVersionTitle",
        editingSong.recordingVersionTitle,
      );

      formData.append("genre", editingSong.genre || "");

      // if (editingSong.artistIds && editingSong.artistIds.length > 0) {
      //   editingSong.artistIds.forEach((artistId) => {
      //     formData.append("artistIds", artistId._id || artistId);
      //   });
      // }

      if (editingSong.themes && editingSong.themes.length > 0) {
        editingSong.themes.forEach((themeId) => {
          formData.append("themes", themeId);
        });
      }

      if (editingSong.coverImage) {
        formData.append("coverImage", editingSong.coverImage);
      }
      if (editingSong.file) {
        formData.append("file", editingSong.file);
      }

      const res = await httpClient.put(
        `admin/media-track/update/${editingSong?._id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      // });

      if (res.data.success) {
        setShowEditSongDialog(false);
        setEditingSong(null);
        await fetchAlbumDetails(selectedAlbum);
        await fetchAlbums(currentPage, searchQuery);
      } else {
        console.error("Failed to update song:", res.data.message);
      }
      // await Promise.all(uploadPromises);
    } catch (error) {
      console.error("Error uploading songs:", error);
    } finally {
      setUploading(false);
    }
  };

  const handleUploadAlbum = async (e) => {
    e.preventDefault();
    const coverImageFile = formData.coverImage;
    const albumFile = formData.file;
    const errors = {};

    if (!formData.title?.trim()) {
      errors.title = "Album title is required.";
    }
    // if (!formData.description?.trim()) {
    //   errors.description = "Description is required.";
    // }

    if (!formData.themes) {
      errors.themes = "Theme is required.";
    }

    if (!formData.releaseDate) {
      errors.releaseDate = "Release date is required.";
    }

    if (!formData.coverImage || !(formData.coverImage instanceof File)) {
      errors.coverImage = "Album cover image is required.";
    }
    if (!formData.file || !(formData.file instanceof File)) {
      errors.file = "Album file is required.";
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setUploadFormErrors(errors);
      return;
    }

    setFormErrors({});
    setUploadFormErrors({});
    setUploading(true);

    try {
      const uploadFormData = new FormData();
      uploadFormData.append("contentType", "album");
      uploadFormData.append("title", formData.title);
      uploadFormData.append("description", formData.description);
      uploadFormData.append("releaseDate", formData.releaseDate);
      uploadFormData.append("isFeatured", "false");
      uploadFormData.append("isActive", "true");
      uploadFormData.append("artistIds", artistIds);
      // uploadFormData.append("genre",)
      if (formData.artist) uploadFormData.append("artist", formData.artist);

      if (formData.themes) uploadFormData.append("themes", formData.themes);

      uploadFormData.append("coverImage", formData.coverImage);
      uploadFormData.append("file", albumFile);

      const response = await httpClient.post(
        "admin/media-collection/add-collection",
        uploadFormData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      if (response.data.success) {
        setShowUploadDialog(false);
        setSearchQuery("");
        resetForm();
        await fetchAlbums(currentPage, searchQuery);
      }
    } catch (error) {
      console.error("Error uploading album:", error);
    } finally {
      setUploading(false);
    }
  };
  console.log("editingSong", editingSong);
  const handleEditSong = (song) => {
    setEditingSong({
      _id: song._id,
      title: song.title,
      description: song.description,
      releaseDate: song.releaseDate,
      artist: song.artist,
      themes: song.themes,
      coverImage: song.coverImage,
      file: song.file,
      fileUrl: song.fileUrl,
      coverImageUrl: song.coverImageUrl,
    });
    setShowEditSongDialog(true);
  };

  const handleDeleteSong = async (song) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Are you sure you want to delete this song?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });
    if (result.isConfirmed)
      try {
        const response = await httpClient.delete(
          `admin/media-track/delete/${song._id}`,
        );
        if (response.data.success) {
          await fetchAlbumDetails(selectedAlbum);
        }
      } catch (error) {
        console.error("Error deleting song:", error);
      } finally {
        setUploading(false);
      }
  };
  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "coverImage" || name === "file") {
      setFormData((prev) => ({
        ...prev,
        [name]: files?.[0] ?? null,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: "" }));
    }
    if (uploadFormErrors[name]) {
      setUploadFormErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      releaseDate: "",
      artist: "",
      themes: "",
      coverImage: null,
      file: null,
    });
    setFormErrors({});
    setUploadFormErrors({ coverImage: "", file: "" });
    setSearchQuery("");
  };

  // Helper functions from upper code
  const getArtistName = (album) => {
    if (album.artist) return album.artist;
    if (album.artistIds && album.artistIds.length > 0) {
      const artist = album.artistIds[0];
      return artist.firstName || artist.name || "Unknown Artist";
    }
    return "Unknown Artist";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "0:00";
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  const getStatusVariant = (status) => {
    switch (status) {
      case "approved":
        return "success";
      case "pending":
        return "warning";
      case "rejected":
        return "danger";
      default:
        return "secondary";
    }
  };

  // Get current page albums
  const getCurrentPageAlbums = () => {
    const startIndex = (currentPage - 1) * limit;
    const endIndex = startIndex + limit;
    return albums.slice(startIndex, endIndex);
  };

  const currentPageAlbums = getCurrentPageAlbums();
  const pendingAlbums = albums.filter((a) => a.isApproved === "pending");
  const approvedAlbums = albums.filter((a) => a.isApproved === "approved");
  const mostPlayed = approvedAlbums.reduce(
    (prev, current) =>
      (prev.playCount || 0) > (current.playCount || 0) ? prev : current,
    approvedAlbums[0] || {},
  );

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  };

  const validateForm = () => {
    let errors = {};
    let isValid = true;

    // Title Validation
    if (!formData.title.trim()) {
      errors.title = "Album title is required.";
      isValid = false;
    }

    // Description Validation
    if (!formData.description.trim()) {
      errors.description = "Description cannot be empty.";
      isValid = false;
    }

    // Cover Image Validation
    if (!formData.coverImage) {
      errors.coverImage = "Please upload an album cover.";
      isValid = false;
    }

    // Release Date Validation
    if (!formData.releaseDate) {
      errors.releaseDate = "Release date is required.";
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  return (
    <div className="content-inner pb-0 container-fluid" id="page_layout">
      <div className="row row--grid">
        <div className="col-12">
          <ul className="breadcrumb">
            <li className="breadcrumb__items">
              <Link to="/artist/dashboard">Dashboard</Link>
            </li>
            <li className="breadcrumb__item breadcrumb__item--active">Album</li>
          </ul>
        </div>

        <div className="col-12">
          <div className="main__title main__title--page d-flex justify-content-between align-items-center">
            <h2 className="card-title text-capitalize mt-3">
              Albums Management
            </h2>
            <button
              className="btn btn-primary"
              onClick={() => {
                resetForm();
                setShowUploadDialog(true);
              }}
            >
              Upload Album
            </button>
          </div>
        </div>

        {/* Most Played Album */}
        <div className="col-12 mt-4">
          <div className="card bg-gradient">
            <div className="card-body">
              <h5 className="card-title">Most Played Album ({period})</h5>
              <div className="row align-items-center">
                <div className="col-md-8">
                  <div className="d-flex align-items-center">
                    <div className="bg-primary bg-opacity-25 p-3 rounded-circle me-3">
                      <i className="bi bi-play-fill text-primary"></i>
                    </div>
                    <div>
                      <h4 className="mb-1">
                        {mostPlayed?.title || "No albums"}
                      </h4>
                      {/* <p className="text-muted mb-0">
                        {getArtistName(mostPlayed)}
                      </p> */}
                    </div>
                  </div>
                </div>
                <div className="col-md-4 text-end">
                  <h3 className="text-primary mb-1">
                    {(mostPlayed?.playCount || 0).toLocaleString()}
                  </h3>
                  <p className="text-muted mb-0">plays</p>
                </div>
              </div>
              <div className="mt-3">
                <div className="btn-group" role="group">
                  <button
                    type="button"
                    className={`btn btn-sm ${period === "week" ? "btn-primary" : "btn-outline-primary"}`}
                    onClick={() => setPeriod("week")}
                  >
                    Week
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${period === "month" ? "btn-primary" : "btn-outline-primary"}`}
                    onClick={() => setPeriod("month")}
                  >
                    Month
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${period === "year" ? "btn-primary" : "btn-outline-primary"}`}
                    onClick={() => setPeriod("year")}
                  >
                    Year
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* All Albums */}
        <div className="col-12 mt-4">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="card-title mb-0">All Albums</h5>
              <span className="text-muted">Total: {totalAlbums} albums</span>
            </div>
            <div className="card-body">
              {loadingAlbums ? (
                <div className="text-center py-4">
                  <p>Loading albums...</p>
                </div>
              ) : currentPageAlbums.length > 0 ? (
                <>
                  <div className="table-responsive">
                    <table className="table table-striped">
                      <thead>
                        <tr>
                          <th>Album Image</th>
                          <th>Title</th>
                          {/* <th>Artist</th> */}
                          <th>Release Date</th>
                          <th>Duration</th>
                          <th>Status</th>
                          <th>Plays</th>
                          <th>No.Songs</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentPageAlbums.map((album) => (
                          <tr key={album._id}>
                            <td>
                              <img
                                src={
                                  album.coverImageUrl ||
                                  "https://via.placeholder.com/50"
                                }
                                alt={album.title}
                                className="rounded"
                                style={{
                                  width: "50px",
                                  height: "50px",
                                  objectFit: "cover",
                                }}
                              />
                            </td>
                            <td className="fw-medium">
                              {album.title.length > 50
                                ? album.title.substring(0, 50) + "..."
                                : album.title}
                            </td>
                            {/* <td>{getArtistName(album)}</td> */}
                            <td>{formatDate(album.releaseDate)}</td>
                            <td>{formatDuration(album.duration)}</td>
                            <td>
                              <span
                                className={`badge bg-${getStatusVariant(album.isApproved)}`}
                              >
                                {album.isApproved || "pending"}
                              </span>
                            </td>
                            <td>{(album.playCount || 0).toLocaleString()}</td>
                            <td>
                              {(album.numberOfItems || 0).toLocaleString()}
                            </td>
                            <td>
                              <div className="btn-group">
                                <button
                                  className="btn btn-outline-info btn-sm"
                                  onClick={() => handleViewClick(album)}
                                  title="View Details"
                                >
                                  <i
                                    className="fa fa-eye"
                                    aria-hidden="true"
                                  ></i>
                                </button>
                                <button
                                  className="btn btn-outline-secondary btn-sm"
                                  onClick={() => openEditModal(album)}
                                  title="Edit Album"
                                >
                                  <i
                                    className="fa fa-pencil"
                                    aria-hidden="true"
                                  ></i>
                                </button>
                                <button
                                  className="btn btn-outline-primary btn-sm"
                                  onClick={() => handleEditClick(album)}
                                  title="Add Songs"
                                >
                                  <i
                                    className="fa fa-plus"
                                    aria-hidden="true"
                                  ></i>
                                </button>
                                <button
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={() => handleDelete(album._id)}
                                  title="Delete"
                                >
                                  <i
                                    className="fa fa-trash"
                                    aria-hidden="true"
                                  ></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="d-flex justify-content-between align-items-center mt-4">
                      <div className="text-muted">
                        Page {currentPage} of {totalPages}
                      </div>
                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-outline-primary btn-sm"
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                        >
                          <i className="bi bi-chevron-left"></i> Previous
                        </button>

                        {getPageNumbers().map((page) => (
                          <button
                            key={page}
                            className={`btn btn-sm ${currentPage === page ? "btn-primary" : "btn-outline-primary"}`}
                            onClick={() => handlePageChange(page)}
                          >
                            {page}
                          </button>
                        ))}

                        <button
                          className="btn btn-outline-primary btn-sm"
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                        >
                          Next <i className="bi bi-chevron-right"></i>
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-4 text-muted">
                  No albums found
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Upload Album Modal */}
      {showUploadDialog && (
        <div className="modal-overlay">
          <div
            className="modal-content zoom-anim-dialog modal modal--form eventss"
            style={{ maxWidth: "700px" }}
          >
            <button
              className="modal__close"
              onClick={() => {
                setShowUploadDialog(false);
                resetForm();
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="24"
                height="24"
              >
                <path d="M13.41,12l4.3-4.29a1,1,0,1,0-1.42-1.42L12,10.59,7.71,6.29A1,1,0,0,0,6.29,7.71L10.59,12l-4.3,4.29a1,1,0,0,0,0,1.42,1,1,0,0,0,1.42,0L12,13.41l4.29,4.3a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.42Z" />
              </svg>
            </button>

            <h3 className="sign__title border-bottom pb-3">Upload Album</h3>

            <form onSubmit={handleUploadAlbum}>
              <div className="sign__group sign__group--row">
                {/* Title */}
                <div className="col-12">
                  <div className="form-group">
                    <label className="form-label" htmlFor="title">
                      Album Title *
                    </label>
                    <input
                      type="text"
                      className={`form-control ${formErrors.title ? "is-invalid" : ""}`}
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      placeholder="Enter album title"
                    />
                    {formErrors.title && (
                      <div className="text-danger small mt-1">
                        {formErrors.title}
                      </div>
                    )}
                  </div>
                </div>

                {/* Description */}
                <div className="col-12">
                  <div className="form-group">
                    <label className="form-label" htmlFor="description">
                      Description
                    </label>
                    <textarea
                      className={`form-control ${formErrors.description ? "is-invalid" : ""}`}
                      id="description"
                      name="description"
                      rows={3}
                      value={formData.description}
                      onChange={handleInputChange}
                      placeholder="Enter album description"
                    />
                    {/* {formErrors.description && (
                      <div className="text-danger small mt-1">
                        {formErrors.description}
                      </div>
                    )} */}
                  </div>
                </div>

                {/* Artist Name */}
                {/* <div className="col-12">
                  <div className="form-group">
                    <label className="form-label" htmlFor="artist">
                      Artist Name *
                    </label>
                    <input
                      type="text"
                      className={`form-control ${formErrors.artist ? "is-invalid" : ""}`}
                      id="artist"
                      name="artist"
                      value={formData.artist}
                      onChange={handleInputChange}
                      placeholder="Enter artist name"
                      required
                    />
                    <div className="form-text">
                      Enter the name of the artist
                    </div>
                    {formErrors.artist && (
                      <div className="text-danger small mt-1">
                        {formErrors.artist}
                      </div>
                    )}
                  </div>
                </div> */}

                {/* Cover Image */}
                <div className="col-12">
                  <div className="form-group">
                    <label className="form-label" htmlFor="coverImage">
                      Album Cover Image *
                    </label>
                    <input
                      type="file"
                      className={`form-control ${uploadFormErrors.coverImage ? "is-invalid" : ""}`}
                      id="coverImage"
                      name="coverImage"
                      accept="image/jpeg, image/png, .jpg, .jpeg, .png"
                      onChange={(e) => {
                        handleInputChange(e);
                        const file = e.target.files?.[0];
                        if (!file) {
                          setUploadFormErrors((prev) => ({
                            ...prev,
                            coverImage: "",
                          }));
                          return;
                        }
                        const ext = (file.name || "")
                          .split(".")
                          .pop()
                          ?.toLowerCase();
                        const type = (file.type || "").toLowerCase();
                        const isImage =
                          ["image/jpeg", "image/png", "image/jpg"].includes(
                            type,
                          ) || ["jpg", "jpeg", "png"].includes(ext);
                        setUploadFormErrors((prev) => ({
                          ...prev,
                          coverImage: isImage
                            ? ""
                            : "Please choose an image file (JPEG or PNG).",
                        }));
                      }}
                    />
                    {uploadFormErrors.coverImage && (
                      <div className="text-danger small mt-1">
                        {uploadFormErrors.coverImage}
                      </div>
                    )}
                  </div>
                </div>

                {/* File Upload */}
                <div className="col-12">
                  <div className="form-group">
                    <label className="form-label" htmlFor="file">
                      Album File *
                    </label>
                    <input
                      type="file"
                      className={`form-control ${uploadFormErrors.file ? "is-invalid" : ""}`}
                      id="file"
                      name="file"
                      accept="audio/mpeg, audio/wav, .mp3, .wav"
                      onChange={(e) => {
                        handleInputChange(e);
                        const file = e.target.files?.[0];
                        if (!file) {
                          setUploadFormErrors((prev) => ({
                            ...prev,
                            file: "",
                          }));
                          return;
                        }
                        const ext = (file.name || "")
                          .split(".")
                          .pop()
                          ?.toLowerCase();
                        const type = (file.type || "").toLowerCase();
                        const isAudio =
                          [
                            "audio/mpeg",
                            "audio/wav",
                            "audio/mp3",
                            "audio/x-wav",
                          ].includes(type) || ["mp3", "wav"].includes(ext);
                        setUploadFormErrors((prev) => ({
                          ...prev,
                          file: isAudio
                            ? ""
                            : "Please choose an audio file (MP3 or WAV).",
                        }));
                      }}
                    />
                    <div className="form-text">
                      Upload the album file (audio/mpeg, audio/wav, .mp3, .wav)
                    </div>
                    {uploadFormErrors.file && (
                      <div className="text-danger small mt-1">
                        {uploadFormErrors.file}
                      </div>
                    )}
                  </div>
                </div>

                {/* Themes */}
                <div className="col-12">
                  <div className="form-group">
                    <label className="form-label" htmlFor="themes">
                      Themes *
                    </label>
                    <select
                      name="themes"
                      className={`form-control ${
                        formErrors.themes ? "is-invalid" : ""
                      }`}
                      value={formData.themes}
                      onChange={handleInputChange}
                      disabled={loadingThemes}
                    >
                      <option value="">
                        {loadingThemes ? "Loading themes..." : "Select theme"}
                      </option>
                      {themes.map((theme) => (
                        <option key={theme._id} value={theme._id}>
                          {theme.name}
                        </option>
                      ))}
                      {themes.length === 0 && !loadingThemes && (
                        <option value="" disabled>
                          No themes available
                        </option>
                      )}
                    </select>
                    {formErrors.themes && (
                      <div className="invalid-feedback d-block">
                        {formErrors.themes}
                      </div>
                    )}
                  </div>
                </div>

                {/* Release Date */}
                <div className="col-12">
                  <div className="form-group">
                    <label className="form-label" htmlFor="releaseDate">
                      Release Date *
                    </label>
                    <input
                      type="date"
                      className={`form-control ${formErrors.releaseDate ? "is-invalid" : ""}`}
                      id="releaseDate"
                      name="releaseDate"
                      value={formData.releaseDate}
                      onChange={handleInputChange}
                    />
                    {formErrors.releaseDate && (
                      <div className="text-danger small mt-1">
                        {formErrors.releaseDate}
                      </div>
                    )}
                  </div>
                </div>

                <div className="col-12 mt-4">
                  <div className="d-flex gap-2 justify-content-end">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => {
                        setShowUploadDialog(false);
                        resetForm();
                      }}
                      disabled={uploading}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={
                        uploading ||
                        !!uploadFormErrors.coverImage ||
                        !!uploadFormErrors.file
                      }
                    >
                      {uploading ? "Uploading..." : "Upload Album"}
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Dialog - Add Songs to Album */}

      {showEditDialog && (
        <div className="modal-overlay">
          <div
            className="modal-content zoom-anim-dialog modal modal--form eventss"
            style={{ maxWidth: "900px" }}
          >
            <button
              className="modal__close"
              onClick={() => {
                setShowEditDialog(false);
                setSongs([]);
                setSelectedAlbum(null);
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="24"
                height="24"
              >
                <path d="M13.41,12l4.3-4.29a1,1,0,1,0-1.42-1.42L12,10.59,7.71,6.29A1,1,0,0,0,6.29,7.71L10.59,12l-4.3,4.29a1,1,0,0,0,0,1.42,1,1,0,0,0,1.42,0L12,13.41l4.29,4.3a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.42Z" />
              </svg>
            </button>

            <h3 className="sign__title border-bottom pb-3">
              Add Songs to Album: {selectedAlbum?.title}
            </h3>

            <div className="space-y-4">
              <div className="d-flex justify-content-between align-items-center mt-3">
                <h5 className="mb-0">Songs List</h5>
                <button
                  onClick={handleAddSong}
                  className="btn btn-primary btn-sm"
                >
                  <i className="bi bi-plus"></i> Add New Song Row
                </button>
              </div>

              {songs.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  No songs added yet. Click "Add Song" to start.
                </div>
              ) : (
                <div className="space-y-3 mt-3">
                  {songs.map((song, index) => (
                    <div
                      className="card p-4 border mb-4 shadow-sm"
                      key={song.id}
                      style={{ background: "#f8f9fa" }}
                    >
                      <div className="d-flex justify-content-between align-items-start mb-3">
                        <h6 className="text-primary">
                          Song #{index + 1} Details
                        </h6>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleRemoveSong(index)}
                        >
                          Remove
                        </button>
                      </div>

                      <div className="row g-3">
                        {/* Song Title & Alt Title */}
                        <div className="col-md-6">
                          <label className="form-label">Song Title *</label>
                          <input
                            type="text"
                            className={`form-control `}
                            value={song.title || ""}
                            onChange={(e) =>
                              handleSongChange(index, "title", e.target.value)
                            }
                            placeholder="Song Title"
                          />
                          {songErrors[index]?.title && (
                            <small className="text-danger">
                              {songErrors[index].title}
                            </small>
                          )}
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">
                            Alternative Title
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={song.alternativeTitle || ""}
                            onChange={(e) =>
                              handleSongChange(
                                index,
                                "alternativeTitle",
                                e.target.value,
                              )
                            }
                          />
                        </div>

                        <div className="col-md-6">
                          <label className="form-label">
                            Recording Version Title *
                          </label>
                          <select
                            className="form-control"
                            value={song.recordingVersionTitle}
                            onChange={(e) =>
                              handleSongChange(
                                index,
                                "recordingVersionTitle",
                                e.target.value,
                              )
                            }
                          >
                            <option value="">Select Version</option>
                            <option value="clean">Clean</option>
                            <option value="radio edit">Radio Edit</option>
                            <option value="explicit">Explicit</option>
                          </select>
                          {songErrors[index]?.recordingVersionTitle && (
                            <small className="text-danger">
                              {songErrors[index].recordingVersionTitle}
                            </small>
                          )}
                        </div>

                        {/* Producer & Release Date */}
                        <div className="col-md-6">
                          <label className="form-label">Producer</label>
                          <input
                            type="text"
                            className="form-control"
                            value={song.recordingRadioProducer || ""}
                            onChange={(e) =>
                              handleSongChange(
                                index,
                                "recordingRadioProducer",
                                e.target.value,
                              )
                            }
                          />
                        </div>

                        <div className="col-md-6">
                          <label className="form-label">Release Date </label>
                          <input
                            type="date"
                            className={`form-control ${songErrors[index]?.releaseDate ? "is-invalid" : ""}`}
                            value={song.releaseDate || ""}
                            onChange={(e) =>
                              handleSongChange(
                                index,
                                "releaseDate",
                                e.target.value,
                              )
                            }
                          />

                          {songErrors[index]?.releaseDate && (
                            <small className="text-danger">
                              {songErrors[index].releaseDate}
                            </small>
                          )}
                        </div>

                        {/* Theme & Catalog */}
                        <div className="col-md-6">
                          <label className="form-label">Theme *</label>
                          <select
                            className={`form-control `}
                            value={song.themes?.[0] || ""}
                            onChange={(e) =>
                              handleSongChange(index, "themes", [
                                e.target.value,
                              ])
                            }
                          >
                            <option value="">Select Theme</option>
                            {themes.map((t) => (
                              <option key={t._id} value={t._id}>
                                {t.name}
                              </option>
                            ))}
                          </select>
                          {songErrors[index]?.themes && (
                            <small className="text-danger">
                              {songErrors[index].themes}
                            </small>
                          )}
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Catalog Number</label>
                          <input
                            type="text"
                            className="form-control"
                            value={song.catalogNumber || ""}
                            onChange={(e) =>
                              handleSongChange(
                                index,
                                "catalogNumber",
                                e.target.value,
                              )
                            }
                          />
                        </div>

                        {/* Label & Writers */}
                        <div className="col-md-6">
                          <label className="form-label">Label Name</label>
                          <input
                            type="text"
                            className="form-control"
                            value={song.LabelName || ""}
                            onChange={(e) =>
                              handleSongChange(
                                index,
                                "LabelName",
                                e.target.value,
                              )
                            }
                          />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Writers</label>
                          <input
                            type="text"
                            className="form-control"
                            value={song.writers || ""}
                            onChange={(e) =>
                              handleSongChange(index, "writers", e.target.value)
                            }
                          />
                        </div>

                        {/* IPI & ISWC */}
                        <div className="col-md-6">
                          <label className="form-label">Writers IPI Code</label>
                          <input
                            type="text"
                            className="form-control"
                            value={song.writersIPICode || ""}
                            onChange={(e) =>
                              handleSongChange(
                                index,
                                "writersIPICode",
                                e.target.value,
                              )
                            }
                          />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">ISWC</label>
                          <input
                            type="text"
                            className="form-control"
                            value={song.iswc || ""}
                            onChange={(e) =>
                              handleSongChange(index, "iswc", e.target.value)
                            }
                          />
                        </div>

                        {/* MLC & UPC */}
                        <div className="col-md-6">
                          <label className="form-label">MLC Song Code</label>
                          <input
                            type="text"
                            className="form-control"
                            value={song.mlcSongCode || ""}
                            onChange={(e) =>
                              handleSongChange(
                                index,
                                "mlcSongCode",
                                e.target.value,
                              )
                            }
                          />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">UPC/EAN</label>
                          <input
                            type="text"
                            className="form-control"
                            value={song.upcEan || ""}
                            onChange={(e) =>
                              handleSongChange(index, "upcEan", e.target.value)
                            }
                          />
                        </div>

                        {/* ISRC & Files */}
                        <div className="col-md-4">
                          <label className="form-label">ISRC</label>
                          <input
                            type="text"
                            className="form-control"
                            value={song.isrc || ""}
                            onChange={(e) =>
                              handleSongChange(index, "isrc", e.target.value)
                            }
                          />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label">Cover Image *</label>
                          <input
                            type="file"
                            className={`form-control ${songErrors[index]?.coverImage ? "is-invalid" : ""}`}
                            accept="image/*"
                            onChange={(e) =>
                              handleSongFileChange(
                                index,
                                "coverImage",
                                e.target.files[0],
                              )
                            }
                          />

                          {/* Error Message for Cover Image */}
                          {songErrors[index]?.coverImage && (
                            <small className="text-danger d-block mt-1">
                              {songErrors[index].coverImage}
                            </small>
                          )}

                          {/* Image Preview logic */}
                          {song.coverImage && (
                            <div className="mt-2 text-center">
                              <img
                                src={URL.createObjectURL(song.coverImage)}
                                alt="Preview"
                                style={{
                                  width: "80px",
                                  height: "80px",
                                  objectFit: "cover",
                                  borderRadius: "5px",
                                  border: "1px solid #ddd",
                                }}
                              />
                              <p className="small text-muted mb-0">
                                Selected Image
                              </p>
                            </div>
                          )}
                        </div>

                        <div className="col-md-4">
                          <label className="form-label">Audio File *</label>
                          <input
                            type="file"
                            className={`form-control ${songErrors[index]?.file ? "is-invalid" : ""} `}
                            accept="audio/*"
                            onChange={(e) =>
                              handleSongFileChange(
                                index,
                                "file",
                                e.target.files[0],
                              )
                            }
                          />
                          {songErrors[index]?.file && (
                            <small className="text-danger">
                              {songErrors[index].file}
                            </small>
                          )}
                          {song.file && (
                            <div className="mt-2 p-2 bg-light border rounded">
                              <span className="small text-success">
                                <i className="fa fa-music me-2"></i>{" "}
                                <strong>Selected:</strong> {song.file.name}
                              </span>
                              <div className="mt-1">
                                <audio
                                  controls
                                  src={song.filePreview}
                                  // src={URL.createObjectURL(song.file)}
                                  style={{ height: "30px", width: "100%" }}
                                />
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="col-12">
                          <label className="form-label">Description</label>
                          <textarea
                            className="form-control"
                            rows="2"
                            value={song.description || ""}
                            onChange={(e) =>
                              handleSongChange(
                                index,
                                "description",
                                e.target.value,
                              )
                            }
                          ></textarea>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="d-flex gap-2 justify-content-end pt-4 border-top mt-3">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => {
                    setShowEditDialog(false);
                    setSongs([]);
                    setSelectedAlbum(null);
                  }}
                  disabled={uploading}
                >
                  Cancel
                </button>
                {/* <button
                  onClick={handleSubmitSongs}
                  className="btn btn-primary"
                  disabled={
                    uploading ||
                    songs.length === 0 ||
                    !songs.every(
                      (s) =>
                        s.title?.trim() &&
                        s.file &&
                        s.themes?.[0] &&
                        s.releaseDate,
                    )
                  }
                >
                  {uploading ? "Uploading..." : `Upload ${songs.length} Songs`}
                </button> */}
                <button
                  onClick={handleSubmitSongs}
                  className="btn btn-primary"
                  disabled={uploading || songs.length === 0}
                >
                  {uploading ? "Uploading..." : `Upload ${songs.length} Songs`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Album Modal */}
      {isEditModalOpen && (
        <div className="modal-overlay" onClick={closeEditModal}>
          <div
            className="modal-content modal-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h5 className="modal-title">Edit Album</h5>
              <button
                type="button"
                className="btn-close"
                onClick={closeEditModal}
                disabled={submitting}
              />
            </div>

            <form onSubmit={handleEditSubmit}>
              <div className="modal-body">
                {/* Title */}
                <div className="col-12">
                  <div className="form-group">
                    <label className="form-label" htmlFor="editTitle">
                      Album Title *
                    </label>
                    <input
                      type="text"
                      className={`form-control ${editFormErrors.title ? "is-invalid" : ""}`}
                      id="editTitle"
                      name="title"
                      value={editFormData.title}
                      onChange={handleEditInputChange}
                      placeholder="Enter album title"
                    />
                    {editFormErrors.title && (
                      <div className="text-danger small mt-1">
                        {editFormErrors.title}
                      </div>
                    )}
                  </div>
                </div>

                {/* Description */}
                <div className="col-12">
                  <div className="form-group">
                    <label className="form-label" htmlFor="editDescription">
                      Description *
                    </label>
                    <textarea
                      className={`form-control ${editFormErrors.description ? "is-invalid" : ""}`}
                      id="editDescription"
                      name="description"
                      rows={3}
                      value={editFormData.description}
                      onChange={handleEditInputChange}
                      placeholder="Enter album description"
                    />
                    {editFormErrors.description && (
                      <div className="text-danger small mt-1">
                        {editFormErrors.description}
                      </div>
                    )}
                  </div>
                </div>

                {/* Artist Name */}
                {/* <div className="col-12">
                  <div className="form-group">
                    <label className="form-label" htmlFor="editArtist">
                      Artist Name *
                    </label>
                    <input
                      type="text"
                      className={`form-control ${editFormErrors.artist ? "is-invalid" : ""}`}
                      id="editArtist"
                      name="artist"
                      value={editFormData.artist}
                      onChange={handleEditInputChange}
                      placeholder="Enter artist name"
                    />
                    <div className="form-text">
                      Enter the name of the artist
                    </div>
                    {editFormErrors.artist && (
                      <div className="text-danger small mt-1">
                        {editFormErrors.artist}
                      </div>
                    )}
                  </div>
                </div> */}

                {/* Current Cover Image & Audio (from API) */}
                {(editingAlbum?.coverImageUrl || editingAlbum?.fileUrl) && (
                  <div className="col-12 mb-3">
                    <div className="row g-3">
                      {editingAlbum.coverImageUrl && (
                        <div className="col-md-6">
                          <label className="form-label d-block">
                            Current Cover Image
                          </label>
                          <img
                            src={editingAlbum.coverImageUrl}
                            alt="Current cover"
                            className="img-fluid rounded border"
                            style={{ maxHeight: "180px", objectFit: "cover" }}
                            onError={(e) => {
                              e.target.src = "https://via.placeholder.com/300";
                            }}
                          />
                        </div>
                      )}
                      {editingAlbum.fileUrl && (
                        <div className="col-md-6">
                          <label className="form-label d-block">
                            Current Album File
                          </label>
                          <audio
                            controls
                            src={editingAlbum.fileUrl}
                            className="w-100"
                            style={{ maxHeight: "48px" }}
                          >
                            Your browser does not support the audio element.
                          </audio>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Cover Image */}
                <div className="col-12">
                  <div className="form-group">
                    <label className="form-label" htmlFor="editCoverImage">
                      Album Cover Image *
                    </label>
                    <input
                      type="file"
                      className={`form-control ${editUploadFormErrors.coverImage ? "is-invalid" : ""}`}
                      id="editCoverImage"
                      name="coverImage"
                      accept="image/jpeg, image/png, .jpg, .jpeg, .png"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) {
                          setEditCoverImageFile(null);
                          setEditUploadFormErrors((prev) => ({
                            ...prev,
                            coverImage: "",
                          }));
                          return;
                        }
                        const ext = (file.name || "")
                          .split(".")
                          .pop()
                          ?.toLowerCase();
                        const type = (file.type || "").toLowerCase();
                        const isImage =
                          ["image/jpeg", "image/png", "image/jpg"].includes(
                            type,
                          ) || ["jpg", "jpeg", "png"].includes(ext);
                        if (isImage) {
                          setEditCoverImageFile(file);
                          setEditUploadFormErrors((prev) => ({
                            ...prev,
                            coverImage: "",
                          }));
                        } else {
                          setEditCoverImageFile(null);
                          setEditUploadFormErrors((prev) => ({
                            ...prev,
                            coverImage:
                              "Please choose an image file (JPEG or PNG).",
                          }));
                        }
                      }}
                    />
                    {editUploadFormErrors.coverImage && (
                      <div className="text-danger small mt-1">
                        {editUploadFormErrors.coverImage}
                      </div>
                    )}
                  </div>
                </div>

                {/* Album File (optional on edit) */}
                <div className="col-12">
                  <div className="form-group">
                    <label className="form-label" htmlFor="editFile">
                      Album File
                    </label>
                    <input
                      type="file"
                      className={`form-control ${editUploadFormErrors.file ? "is-invalid" : ""}`}
                      id="editFile"
                      name="file"
                      accept="audio/mpeg, audio/wav, .mp3, .wav"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) {
                          setEditAlbumFile(null);
                          setEditUploadFormErrors((prev) => ({
                            ...prev,
                            file: "",
                          }));
                          return;
                        }
                        const ext = (file.name || "")
                          .split(".")
                          .pop()
                          ?.toLowerCase();
                        const type = (file.type || "").toLowerCase();
                        const isAudio =
                          [
                            "audio/mpeg",
                            "audio/wav",
                            "audio/mp3",
                            "audio/x-wav",
                          ].includes(type) || ["mp3", "wav"].includes(ext);
                        if (isAudio) {
                          setEditAlbumFile(file);
                          setEditUploadFormErrors((prev) => ({
                            ...prev,
                            file: "",
                          }));
                        } else {
                          setEditAlbumFile(null);
                          setEditUploadFormErrors((prev) => ({
                            ...prev,
                            file: "Please choose an audio file (MP3 or WAV).",
                          }));
                        }
                      }}
                    />
                    <div className="form-text">
                      Upload a new file to replace (optional)
                    </div>
                    {editUploadFormErrors.file && (
                      <div className="text-danger small mt-1">
                        {editUploadFormErrors.file}
                      </div>
                    )}
                  </div>
                </div>

                {/* Theme */}
                <div className="col-12">
                  <div className="form-group">
                    <label className="form-label" htmlFor="editThemes">
                      Album Theme
                    </label>
                    <select
                      name="themes"
                      className={`form-control ${editFormErrors.themes ? "is-invalid" : ""}`}
                      id="editThemes"
                      value={editFormData.themes}
                      onChange={handleEditInputChange}
                      disabled={loadingThemes}
                    >
                      <option value="">
                        {loadingThemes ? "Loading themes..." : "Select theme"}
                      </option>
                      {themes.map((t) => (
                        <option key={t._id} value={t._id}>
                          {t.name}
                        </option>
                      ))}
                      {themes.length === 0 && !loadingThemes && (
                        <option value="" disabled>
                          No themes available
                        </option>
                      )}
                    </select>
                    {/* <div className="form-text">Select a theme (optional)</div> */}
                    {editFormErrors.themes && (
                      <div className="text-danger small mt-1">
                        {editFormErrors.themes}
                      </div>
                    )}
                  </div>
                </div>

                {/* Release Date */}
                <div className="col-12">
                  <div className="form-group">
                    <label className="form-label" htmlFor="editReleaseDate">
                      Release Date
                    </label>
                    <input
                      type="date"
                      className={`form-control ${editFormErrors.releaseDate ? "is-invalid" : ""}`}
                      id="editReleaseDate"
                      name="releaseDate"
                      value={editFormData.releaseDate}
                      onChange={handleEditInputChange}
                    />
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeEditModal}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={
                    submitting ||
                    !!editUploadFormErrors.coverImage ||
                    !!editUploadFormErrors.file
                  }
                >
                  {submitting ? "Updating..." : "Update Album"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Album Details Modal */}
      {selectedAlbumDetails && (
        <div className="modal-overlay">
          <div
            className="modal-content zoom-anim-dialog modal modal--form eventss"
            style={{ maxWidth: "800px" }}
          >
            <button
              className="modal__close"
              onClick={() => setSelectedAlbumDetails(null)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="24"
                height="24"
              >
                <path d="M13.41,12l4.3-4.29a1,1,0,1,0-1.42-1.42L12,10.59,7.71,6.29A1,1,0,0,0,6.29,7.71L10.59,12l-4.3,4.29a1,1,0,0,0,0,1.42,1,1,0,0,0,1.42,0L12,13.41l4.29,4.3a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.42Z" />
              </svg>
            </button>

            <h3 className="sign__title border-bottom pb-3">Album Details</h3>

            {loadingAlbumDetails ? (
              <div className="text-center py-4">
                <p>Loading album details...</p>
              </div>
            ) : selectedAlbumDetails ? (
              <div className="space-y-4">
                {/* Album summary: title, artist, theme (mapped to name), etc. */}
                <div className="row mb-3">
                  <div className="col-md-6 mb-2">
                    <strong>Title:</strong>
                    <p className="mb-0">{selectedAlbumDetails.title}</p>
                  </div>
                  {/* <div className="col-md-6 mb-2">
                    <strong>Artist:</strong>
                    <p className="mb-0">
                      {getArtistName(selectedAlbumDetails)}
                    </p>
                  </div> */}
                  <div className="col-md-6 mb-2">
                    <strong>Theme:</strong>
                    <p className="mb-0">
                      {(() => {
                        const t = selectedAlbumDetails.themes;
                        if (!t) return "—";
                        if (Array.isArray(t)) {
                          const names = t
                            .map((id) => themes.find((x) => x._id === id)?.name)
                            .filter(Boolean);
                          return names.length ? names.join(", ") : "—";
                        }
                        return themes.find((x) => x._id === t)?.name ?? t;
                      })()}
                    </p>
                  </div>
                  <div className="col-md-6 mb-2">
                    <strong>Release Date:</strong>
                    <p className="mb-0">
                      {formatDate(selectedAlbumDetails.releaseDate)}
                    </p>
                  </div>
                </div>

                {/* Songs List */}
                <div className="border-top pt-4">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">Songs in this Album</h5>
                    <span className="badge bg-secondary">
                      {albumSongs.length} songs
                    </span>
                  </div>

                  {loadingAlbumSongs ? (
                    <div className="text-center py-4">
                      <p>Loading songs...</p>
                    </div>
                  ) : albumSongs.length > 0 ? (
                    <div
                      className="space-y-3"
                      style={{ maxHeight: "400px", overflowY: "auto" }}
                    >
                      {albumSongs.map((song, index) => (
                        <div className="card p-3" key={song._id}>
                          <div className="d-flex align-items-center justify-content-between">
                            <div className="d-flex align-items-center flex-grow-1">
                              <div
                                className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-3"
                                style={{ width: "32px", height: "32px" }}
                              >
                                <span className="text-primary fw-medium">
                                  {index + 1}
                                </span>
                              </div>

                              {song.coverImageUrl && (
                                <img
                                  src={song.coverImageUrl}
                                  alt={song.title}
                                  className="rounded me-3"
                                  style={{
                                    width: "48px",
                                    height: "48px",
                                    objectFit: "cover",
                                  }}
                                />
                              )}

                              <div className="flex-grow-1">
                                <p className="fw-medium mb-1">
                                  {song.title.length > 30
                                    ? song.title.substring(0, 30) + "..."
                                    : song.title}
                                </p>
                                <div className="d-flex gap-3">
                                  <small className="text-muted">
                                    Duration: {formatDuration(song.duration)}
                                  </small>
                                  <small className="text-muted">
                                    Release: {formatDate(song.releaseDate)}
                                  </small>
                                  <span
                                    className={`badge bg-${getStatusVariant(song.isApproved)}`}
                                  >
                                    {song.isApproved}
                                  </span>
                                </div>
                              </div>
                            </div>

                            <div className="d-flex align-items-center gap-2">
                              {song.fileUrl && (
                                <audio
                                  controls
                                  style={{ width: "200px", height: "32px" }}
                                >
                                  <source
                                    src={song.fileUrl}
                                    type="audio/mpeg"
                                  />
                                </audio>
                              )}
                              <div className="text-end">
                                <small className="text-muted">
                                  {(song.playCount || 0).toLocaleString()} plays
                                </small>
                              </div>
                            </div>
                          </div>

                          <div className="d-flex justify-content-between align-items-center border-top pt-2 mt-2">
                            {song.description && (
                              <div className="description">
                                <p className="text-muted small mb-0">
                                  {song.description}
                                </p>
                              </div>
                            )}
                            <div className="d-flex align-items-center gap-2 ms-auto ">
                              <button
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => handleEditSong(song)}
                              >
                                <i
                                  className="fa fa-pencil"
                                  aria-hidden="true"
                                ></i>
                              </button>
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleDeleteSong(song)}
                              >
                                <i
                                  className="fa fa-trash"
                                  aria-hidden="true"
                                ></i>
                              </button>
                            </div>
                          </div>

                          {(() => {
                            const themeIds = Array.isArray(song.themes)
                              ? song.themes
                              : song.themes
                                ? [song.themes]
                                : [];
                            if (themeIds.length === 0) return null;
                            return (
                              <div className="mt-2 d-flex flex-wrap gap-1">
                                {themeIds.map((themeId) => {
                                  const theme = themes.find(
                                    (t) => t._id === themeId,
                                  );
                                  return theme ? (
                                    <span
                                      key={themeId}
                                      className="badge bg-light text-dark border"
                                    >
                                      {theme.name}
                                    </span>
                                  ) : null;
                                })}
                              </div>
                            );
                          })()}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4 text-muted border rounded">
                      <p>No songs found in this album</p>
                      <p className="small mt-1">
                        Add songs using the Edit button
                      </p>
                    </div>
                  )}
                </div>

                <div className="d-flex justify-content-end gap-2 pt-4 border-top">
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => setSelectedAlbumDetails(null)}
                  >
                    Close
                  </button>
                  {/* {selectedAlbumDetails.isApproved === "pending" && (
                    <>
                      <button 
                        className="btn btn-success"
                        onClick={() => {
                          handleApprove(selectedAlbumDetails._id);
                          setSelectedAlbumDetails(null);
                        }}
                      >
                        Approve
                      </button>
                      <button 
                        className="btn btn-danger"
                        onClick={() => {
                          handleReject(selectedAlbumDetails._id);
                          setSelectedAlbumDetails(null);
                        }}
                      >
                        Reject
                      </button>
                    </>
                  )} */}
                  <button
                    className="btn btn-outline-danger"
                    onClick={() => {
                      handleDelete(selectedAlbumDetails._id);
                      setSelectedAlbumDetails(null);
                    }}
                  >
                    Delete Album
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center py-4 text-muted">
                Failed to load album details
              </div>
            )}
          </div>
        </div>
      )}

      {isEditModalOpen && (
        <div className="modal-overlay" onClick={closeEditModal}>
          <div
            className="modal-content modal-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header d-flex justify-content-between align-items-center mb-3">
              <h5 className="modal-title">Edit Album</h5>
              <button
                type="button"
                className="btn-close"
                onClick={closeEditModal}
                disabled={submitting}
              ></button>
            </div>

            <form onSubmit={handleEditSubmit}>
              <div className="modal-body">
                {/* Title */}
                <div className="col-12">
                  <div className="form-group">
                    <label className="form-label" htmlFor="editTitle">
                      Album Title *
                    </label>
                    <input
                      type="text"
                      className={`form-control ${editFormErrors.title ? "is-invalid" : ""}`}
                      id="editTitle"
                      name="title"
                      value={editFormData.title}
                      onChange={handleEditInputChange}
                      placeholder="Enter album title"
                    />
                    {editFormErrors.title && (
                      <div className="text-danger small mt-1">
                        {editFormErrors.title}
                      </div>
                    )}
                  </div>
                </div>

                {/* Description */}
                <div className="col-12">
                  <div className="form-group">
                    <label className="form-label" htmlFor="editDescription">
                      Description *
                    </label>
                    <textarea
                      className={`form-control ${editFormErrors.description ? "is-invalid" : ""}`}
                      id="editDescription"
                      name="description"
                      rows={3}
                      value={editFormData.description}
                      onChange={handleEditInputChange}
                      placeholder="Enter album description"
                    />
                    {editFormErrors.description && (
                      <div className="text-danger small mt-1">
                        {editFormErrors.description}
                      </div>
                    )}
                  </div>
                </div>

                {/* Artist Name */}
                {/* <div className="col-12">
                  <div className="form-group">
                    <label className="form-label" htmlFor="editArtist">
                      Artist Name *
                    </label>
                    <input
                      type="text"
                      className={`form-control ${editFormErrors.artist ? "is-invalid" : ""}`}
                      id="editArtist"
                      name="artist"
                      value={editFormData.artist}
                      onChange={handleEditInputChange}
                      placeholder="Enter artist name"
                    />
                    <div className="form-text">
                      Enter the name of the artist
                    </div>
                    {editFormErrors.artist && (
                      <div className="text-danger small mt-1">
                        {editFormErrors.artist}
                      </div>
                    )}
                  </div>
                </div> */}

                {/* Theme */}
                <div className="col-12">
                  <div className="form-group">
                    <label className="form-label" htmlFor="editThemes2">
                      Album Theme *
                    </label>
                    <select
                      name="themes"
                      className={`form-control ${editFormErrors.themes ? "is-invalid" : ""}`}
                      value={editFormData.themes}
                      onChange={handleEditInputChange}
                      disabled={loadingThemes}
                    >
                      <option value="">
                        {loadingThemes ? "Loading themes..." : "Select theme"}
                      </option>
                      {themes.map((t) => (
                        <option key={t._id} value={t._id}>
                          {t.name}
                        </option>
                      ))}
                      {themes.length === 0 && !loadingThemes && (
                        <option value="" disabled>
                          No themes available
                        </option>
                      )}
                    </select>
                    {/* <div className="form-text">Select a theme (optional)</div> */}
                    {editFormErrors.themes && (
                      <div className="text-danger small mt-1">
                        {editFormErrors.themes}
                      </div>
                    )}
                  </div>
                </div>

                {/* Release Date */}
                <div className="col-12">
                  <div className="form-group">
                    <label className="form-label" htmlFor="editReleaseDate">
                      Release Date *
                    </label>
                    <input
                      type="date"
                      className={`form-control ${editFormErrors.releaseDate ? "is-invalid" : ""}`}
                      id="editReleaseDate"
                      name="releaseDate"
                      value={editFormData.releaseDate}
                      onChange={handleEditInputChange}
                    />
                    {editFormErrors.releaseDate && (
                      <div className="text-danger small mt-1">
                        {editFormErrors.releaseDate}
                      </div>
                    )}
                  </div>
                </div>
                {/* Current Cover Image & Audio (from API) */}
                {(editingAlbum?.coverImageUrl || editingAlbum?.fileUrl) && (
                  <div className="col-12 mb-3">
                    <div className="row g-3"></div>
                  </div>
                )}

                {/* Cover Image */}
                <div className="col-12">
                  <div className="form-group">
                    <label className="form-label" htmlFor="editCoverImage2">
                      Album Cover Image *
                    </label>
                    <input
                      type="file"
                      className={`form-control ${editUploadFormErrors.coverImage ? "is-invalid" : ""}`}
                      id="editCoverImage2"
                      name="coverImage"
                      accept="image/jpeg, image/png, .jpg, .jpeg, .png"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) {
                          setEditCoverImageFile(null);
                          setEditUploadFormErrors((prev) => ({
                            ...prev,
                            coverImage: "",
                          }));
                          return;
                        }
                        const ext = (file.name || "")
                          .split(".")
                          .pop()
                          ?.toLowerCase();
                        const type = (file.type || "").toLowerCase();
                        const isImage =
                          ["image/jpeg", "image/png", "image/jpg"].includes(
                            type,
                          ) || ["jpg", "jpeg", "png"].includes(ext);
                        if (isImage) {
                          setEditCoverImageFile(file);
                          setEditUploadFormErrors((prev) => ({
                            ...prev,
                            coverImage: "",
                          }));
                        } else {
                          setEditCoverImageFile(null);
                          setEditUploadFormErrors((prev) => ({
                            ...prev,
                            coverImage:
                              "Please choose an image file (JPEG or PNG).",
                          }));
                        }
                      }}
                    />
                    <div className="form-text">
                      Upload a new cover image to replace (optional)
                    </div>
                    {editUploadFormErrors.coverImage && (
                      <div className="text-danger small mt-1">
                        {editUploadFormErrors.coverImage}
                      </div>
                    )}
                  </div>
                  {editingAlbum.coverImageUrl && (
                    <div className="col-md-6">
                      {/* <label className="form-label d-block">
                        Current Cover Image
                      </label> */}
                      <img
                        src={editingAlbum.coverImageUrl}
                        alt="Current cover"
                        className="img-fluid rounded border"
                        style={{ maxHeight: "180px", objectFit: "cover" }}
                        onError={(e) => {
                          e.target.src = "https://via.placeholder.com/300";
                        }}
                      />
                    </div>
                  )}
                </div>

                {/* File Upload (optional on edit) */}
                <div className="col-12 mt-3">
                  <div className="form-group">
                    <label className="form-label" htmlFor="editFile">
                      Album File
                    </label>
                    <input
                      type="file"
                      className={`form-control ${editUploadFormErrors.file ? "is-invalid" : ""}`}
                      id="editFile"
                      name="file"
                      accept="audio/mpeg, audio/wav, .mp3, .wav"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) {
                          setEditAlbumFile(null);
                          setEditUploadFormErrors((prev) => ({
                            ...prev,
                            file: "",
                          }));
                          return;
                        }
                        const ext = (file.name || "")
                          .split(".")
                          .pop()
                          ?.toLowerCase();
                        const type = (file.type || "").toLowerCase();
                        const isAudio =
                          [
                            "audio/mpeg",
                            "audio/wav",
                            "audio/mp3",
                            "audio/x-wav",
                          ].includes(type) || ["mp3", "wav"].includes(ext);
                        if (isAudio) {
                          setEditAlbumFile(file);
                          setEditUploadFormErrors((prev) => ({
                            ...prev,
                            file: "",
                          }));
                        } else {
                          setEditAlbumFile(null);
                          setEditUploadFormErrors((prev) => ({
                            ...prev,
                            file: "Please choose an audio file (MP3 or WAV).",
                          }));
                        }
                      }}
                    />
                    <div className="form-text">
                      Upload a new file to replace (optional)
                    </div>
                    {editUploadFormErrors.file && (
                      <div className="text-danger small mt-1">
                        {editUploadFormErrors.file}
                      </div>
                    )}
                  </div>
                  {editingAlbum.fileUrl && (
                    <div className="col-md-6">
                      {/* <label className="form-label d-block">
                        Current Album File
                      </label> */}
                      <audio
                        controls
                        src={editingAlbum.fileUrl}
                        className="w-100"
                        style={{ maxHeight: "48px" }}
                      >
                        Your browser does not support the audio element.
                      </audio>
                    </div>
                  )}
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeEditModal}
                  disabled={submitting}
                  style={{ marginRight: "10px" }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={
                    submitting ||
                    !!editUploadFormErrors.coverImage ||
                    !!editUploadFormErrors.file
                  }
                >
                  {submitting ? "Updating..." : "Update Album"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditSongDialog && (
        <div className="modal-overlay">
          <div
            className="modal-content zoom-anim-dialog modal modal--form eventss"
            style={{ maxWidth: "1000px" }}
          >
            <button
              className="modal__close"
              onClick={() => {
                setShowEditSongDialog(false);
                setEditingSong(null);
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="24"
                height="24"
              >
                <path d="M13.41,12l4.3-4.29a1,1,0,1,0-1.42-1.42L12,10.59,7.71,6.29A1,1,0,0,0,6.29,7.71L10.59,12l-4.3,4.29a1,1,0,0,0,0,1.42,1,1,0,0,0,1.42,0L12,13.41l4.29,4.3a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.42Z" />
              </svg>
            </button>

            <h3 className="sign__title border-bottom pb-3">
              Edit Complete Song Details
            </h3>

            <div className="card-body mt-4">
              <div className="row g-3">
                {/* Main Details */}
                <div className="col-md-6">
                  <label className="form-label">Song Title *</label>
                  <input
                    type="text"
                    className={`form-control `}
                    value={editingSong.title || ""}
                    onChange={(e) =>
                      setEditingSong({ ...editingSong, title: e.target.value })
                    }
                  />
                  {editSongErrors.title && (
                    <small className="text-danger">
                      {editSongErrors.title}
                    </small>
                  )}
                </div>

                <div className="col-md-6">
                  <label className="form-label">Alternative Title</label>
                  <input
                    type="text"
                    className="form-control"
                    value={editingSong.alternativeTitle || ""}
                    onChange={(e) =>
                      setEditingSong({
                        ...editingSong,
                        alternativeTitle: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label">
                    Recording Version Title (Remix/Live) *
                  </label>
                  <select
                    className="form-control"
                    value={editingSong.recordingVersionTitle || ""}
                    onChange={(e) =>
                      setEditingSong({
                        ...editingSong,
                        recordingVersionTitle: e.target.value,
                      })
                    }
                  >
                    <option value="">Select Version</option>
                    <option value="clean">Clean</option>
                    <option value="radio edit">Radio Edit</option>
                    <option value="explicit">Explicit</option>
                  </select>
                  {editSongErrors.recordingVersionTitle && (
                    <small className="text-danger">
                      {editSongErrors.recordingVersionTitle}
                    </small>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label">Release Date *</label>
                  <input
                    type="date"
                    className={`form-control `}
                    value={editingSong.releaseDate?.split("T")[0] || ""}
                    onChange={(e) =>
                      setEditingSong({
                        ...editingSong,
                        releaseDate: e.target.value,
                      })
                    }
                  />
                  {editSongErrors.releaseDate && (
                    <small className="text-danger">
                      {editSongErrors.releaseDate}
                    </small>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label">Theme *</label>
                  <select
                    className={`form-control `}
                    value={editingSong.themes?.[0] || ""}
                    onChange={(e) =>
                      setEditingSong({
                        ...editingSong,
                        themes: [e.target.value],
                      })
                    }
                  >
                    <option value="">Select Theme</option>
                    {themes.map((t) => (
                      <option key={t._id} value={t._id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                  {editSongErrors.themes && (
                    <small className="text-danger">
                      {editSongErrors.themes}
                    </small>
                  )}
                </div>

                {/* Industry Codes */}
                <div className="col-md-4">
                  <label className="form-label">ISRC</label>
                  <input
                    type="text"
                    className="form-control"
                    value={editingSong.isrc || ""}
                    onChange={(e) =>
                      setEditingSong({ ...editingSong, isrc: e.target.value })
                    }
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">ISWC</label>
                  <input
                    type="text"
                    className="form-control"
                    value={editingSong.iswc || ""}
                    onChange={(e) =>
                      setEditingSong({ ...editingSong, iswc: e.target.value })
                    }
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">UPC/EAN</label>
                  <input
                    type="text"
                    className="form-control"
                    value={editingSong.upcEan || ""}
                    onChange={(e) =>
                      setEditingSong({ ...editingSong, upcEan: e.target.value })
                    }
                  />
                </div>

                {/* Credits */}
                <div className="col-md-6">
                  <label className="form-label">Writers</label>
                  <input
                    type="text"
                    className="form-control"
                    value={editingSong.writers || ""}
                    onChange={(e) =>
                      setEditingSong({
                        ...editingSong,
                        writers: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Writers IPI Code</label>
                  <input
                    type="text"
                    className="form-control"
                    value={editingSong.writersIPICode || ""}
                    onChange={(e) =>
                      setEditingSong({
                        ...editingSong,
                        writersIPICode: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Producer</label>
                  <input
                    type="text"
                    className="form-control"
                    value={editingSong.recordingRadioProducer || ""}
                    onChange={(e) =>
                      setEditingSong({
                        ...editingSong,
                        recordingRadioProducer: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Label Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={editingSong.LabelName || ""}
                    onChange={(e) =>
                      setEditingSong({
                        ...editingSong,
                        LabelName: e.target.value,
                      })
                    }
                  />
                </div>

                {/* Files & Description */}
                <div className="col-md-12">
                  <label className="form-label">Description *</label>
                  <textarea
                    className="form-control"
                    rows="2"
                    value={editingSong.description || ""}
                    onChange={(e) =>
                      setEditingSong({
                        ...editingSong,
                        description: e.target.value,
                      })
                    }
                  ></textarea>
                  {editSongErrors.description && (
                    <small className="text-danger">
                      {editSongErrors.description}
                    </small>
                  )}
                </div>
                {/* Cover Image Section */}
                <div className="col-md-6">
                  <label className="form-label">Update Cover Image *</label>
                  <input
                    type="file"
                    className={`form-control ${editSongErrors.coverImage ? "is-invalid" : ""}`}
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      // Nayi image select hote hi preview update ho jayega
                      setEditingSong({
                        ...editingSong,
                        coverImage: file,
                        // Ye temporary URL banata hai preview dikhane ke liye
                        tempPreview: URL.createObjectURL(file),
                      });
                      if (editSongErrors.coverImage)
                        setEditSongErrors({
                          ...editSongErrors,
                          coverImage: "",
                        });
                    }}
                  />
                  {editSongErrors.coverImage && (
                    <small className="text-danger">
                      {editSongErrors.coverImage}
                    </small>
                  )}

                  {/* Existing or New Image Preview */}
                  {(editingSong.tempPreview ||
                    editingSong.coverImageUrl ||
                    editingSong.image) && (
                    <div
                      className="mt-2 p-2 border rounded bg-light"
                      style={{ width: "fit-content" }}
                    >
                      <p className="small text-muted mb-1">
                        Current/New Preview:
                      </p>
                      <img
                        src={
                          editingSong.tempPreview ||
                          editingSong.coverImageUrl ||
                          editingSong.image
                        }
                        alt="Cover Preview"
                        style={{
                          height: "80px",
                          width: "80px",
                          objectFit: "cover",
                          borderRadius: "5px",
                        }}
                      />
                    </div>
                  )}
                </div>

                {/* Audio File Section */}
                <div className="col-md-6">
                  <label className="form-label">Update Audio File *</label>
                  <input
                    type="file"
                    className={`form-control ${editSongErrors.audioFile ? "is-invalid" : ""}`}
                    accept="audio/*"
                    onChange={(e) => {
                      setEditingSong({
                        ...editingSong,
                        file: e.target.files[0],
                      });
                      if (editSongErrors.audioFile)
                        setEditSongErrors({ ...editSongErrors, audioFile: "" });
                    }}
                  />
                  {editSongErrors.audioFile && (
                    <small className="text-danger">
                      {editSongErrors.audioFile}
                    </small>
                  )}

                  {/* Existing Audio Player */}
                  {editingSong.fileUrl && (
                    <div className="mt-2">
                      <p className="small text-muted mb-1">Current Audio:</p>
                      <audio controls style={{ height: "35px", width: "100%" }}>
                        <source src={editingSong.fileUrl} type="audio/mpeg" />
                        Your browser does not support the audio element.
                      </audio>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="d-flex gap-2 justify-content-end pt-4 border-top mt-3">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setShowEditSongDialog(false)}
              >
                Cancel
              </button>

              <button
                onClick={handleEditSongs}
                className="btn btn-primary"
                disabled={uploading}
              >
                {uploading ? "Saving..." : "Update Everything"}
              </button>
            </div>
          </div>
        </div>
      )}
      <style jsx>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }

        .modal-content {
          background: #fff;
          border-radius: 12px;
          padding: 30px;
          position: relative;
          max-width: 600px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
        }

        .modal__close {
          position: absolute;
          top: 20px;
          right: 20px;
          background: none;
          border: none;
          cursor: pointer;
          padding: 0;
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .modal__close svg {
          fill: #666;
          transition: fill 0.3s ease;
        }

        .modal__close:hover svg {
          fill: #000;
        }

        .sign__title {
          font-size: 24px;
          font-weight: 600;
          margin-bottom: 25px;
          color: #2f2f2f;
        }

        .sign__group--row {
          display: flex;
          flex-wrap: wrap;
          margin: 0 -15px;
        }

        .form-group {
          margin-bottom: 20px;
          width: 100%;
        }

        .form-label {
          display: block;
          margin-bottom: 8px;
          font-weight: 500;
          color: #2f2f2f;
        }

        .form-control {
          width: 100%;
          padding: 12px 15px;
          border: 1px solid #e1e1e1;
          border-radius: 8px;
          font-size: 14px;
          transition: all 0.3s ease;
        }

        .form-control:focus {
          border-color: #007bff;
          box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.1);
          outline: none;
        }

        .form-text {
          font-size: 12px;
          color: #6c757d;
          margin-top: 5px;
        }

        .btn {
          padding: 10px 20px;
          border-radius: 8px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .btn-primary {
          background: #007bff;
          color: white;
          border: none;
        }

        .btn-primary:hover:not(:disabled) {
          background: #0056b3;
        }

        .btn-outline-primary {
          background: transparent;
          color: #007bff;
          border: 1px solid #007bff;
        }

        .btn-outline-primary:hover:not(:disabled) {
          background: #007bff;
          color: white;
        }

        .btn-outline-secondary {
          background: transparent;
          color: #6c757d;
          border: 1px solid #6c757d;
        }

        .btn-outline-secondary:hover:not(:disabled) {
          background: #6c757d;
          color: white;
        }

        .btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .zoom-anim-dialog {
          animation: zoomIn 0.3s ease;
        }

        @keyframes zoomIn {
          from {
            opacity: 0;
            transform: scale(0.8);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .bg-gradient {
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
        }
      `}</style>
    </div>
  );
}

export default Album;
